import { StudentLayout } from "@jambushrushti/components/learning/StudentLayout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Award, BookOpen, Trophy, Star } from "lucide-react";
import {
  useCertificates,
  type LMSCertificate,
} from "@jambushrushti/hooks/useLMSCertificates";
import { useCourses } from "@jambushrushti/hooks/useLMS";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import { CertificateCard } from "@jambushrushti/components/learning/CertificateCard";
import { generateCertificatePDF } from "@jambushrushti/lib/certificateGenerator";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";

export default function MyCertificates() {
  const { user } = useAdminAuth();
  const { data: certificates, isLoading: loadingCerts } = useCertificates(
    user?.id
  );
  const { data: courses } = useCourses();

  const getCourseName = (courseId: string | null) => {
    if (!courseId || !courses) {
      return "Course Completion";
    }
    const course = courses.find(c => c.id === courseId);
    return course?.title || "Course Completion";
  };

  const handleDownload = (certificate: LMSCertificate) => {
    generateCertificatePDF({
      certificate,
      studentName: user?.email || "Student",
      courseName: getCourseName(certificate.course_id),
    });
  };

  const displayCertificates = certificates ?? [];

  return (
    <StudentLayout title="My Certificates">
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8">
          <div className="relative z-10">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-xl bg-primary/20 p-3">
                <Trophy className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-foreground">
                  My Certificates
                </h1>
                <p className="text-muted-foreground">
                  Your achievements and course completions
                </p>
              </div>
            </div>
          </div>
          <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-primary/10 p-3">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {displayCertificates.length}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Certificates
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-green-500/10 p-3">
                <BookOpen className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {displayCertificates.length}
                </p>
                <p className="text-sm text-muted-foreground">
                  Courses Completed
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/10">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-yellow-500/10 p-3">
                <Star className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">Excellence</p>
                <p className="text-sm text-muted-foreground">
                  Achievement Level
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Certificates Grid */}
        <div>
          <h2 className="font-display mb-6 text-xl font-semibold text-foreground">
            Your Certificates
          </h2>

          {loadingCerts ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => (
                <Card key={i} className="border-primary/10">
                  <CardContent className="space-y-4 p-6">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : displayCertificates.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {displayCertificates.map(cert => (
                <CertificateCard
                  key={cert.id}
                  certificate={cert}
                  courseName={getCourseName(cert.course_id)}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          ) : (
            <Card className="border-primary/10">
              <CardContent className="p-12 text-center">
                <div className="mx-auto mb-4 w-fit rounded-full bg-primary/10 p-4">
                  <Award className="h-12 w-12 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">
                  No Certificates Yet
                </h3>
                <p className="mx-auto max-w-md text-muted-foreground">
                  Complete courses to earn certificates. Your achievements will
                  be displayed here.
                </p>
                <Button className="mt-6" asChild>
                  <a href="/learning/courses">Browse Courses</a>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </StudentLayout>
  );
}
